import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Visibility as Eye,
  Edit,
  MoreVert as MoreVertical,
  CreditCard,
} from '@mui/icons-material';
import { useRouter } from '@cvt/routing';

import { DialogContext } from '@cvt/contexts';
import { getImagePreviewFromFileOrUrl } from '@modules/Tributes/helpers/media';
import StatusBadge from './StatusBadge';

interface TributeCardProps {
    tribute: Tributes.Tribute;
}


const getPrimaryAction = (
  tribute: Tributes.Tribute,
  router: ReturnType<typeof useRouter>,
) => {
  switch (tribute.status) {
  case 'published':
    return {
      label: 'View LGCY',
      href: router.tributes.view(tribute.id).path,
      Icon: Eye,
    };
  case 'draft':
    return {
      label: 'Continue Editing',
      href: router.tributes.edit(tribute.id).path,
      Icon: Edit,
    };
  default:
    return {
      label: 'View LGCY',
      href: router.tributes.view(tribute.id).path,
      Icon: Eye,
    };
  }
};

export const TributeCard: FC<TributeCardProps> = ({ tribute }) => {
  const router = useRouter();
  const primaryAction = getPrimaryAction(tribute, router);
  const createdAt = tribute.created
    ? new Date(tribute.created).toLocaleDateString()
    : '';
  const [mainPhotoPreview, setMainPhotoPreview] = useState<string>(tribute.mainPhoto?.file || '');

  useEffect(() => {
    let revoked = false;
    let previewUrl: string | undefined;

    const loadMainPhotoPreview = async () => {
      const mainPhotoUrl = tribute.mainPhoto?.file;
      if (!mainPhotoUrl) {
        setMainPhotoPreview('');
        return;
      }

      const preview = await getImagePreviewFromFileOrUrl(mainPhotoUrl);
      previewUrl = preview;
      setMainPhotoPreview(preview);
    };

    loadMainPhotoPreview();

    return () => {
      if (previewUrl?.startsWith('blob:') && !revoked) {
        URL.revokeObjectURL(previewUrl);
        revoked = true;
      }
    };
  }, [tribute.mainPhoto?.file]);

  /* menu state */
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const { openDialog } = useContext(DialogContext);
  const onCreateCard = useCallback(() => {
    openDialog('createTributeCard', {
      tribute,
    });
  }, [openDialog, tribute]);
  return (
    <Card
      sx={{
        width: 320,
        maxWidth: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: 2,
        transition: 'box-shadow .2s',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardMedia
        component="img"
        height="220"
        image={mainPhotoPreview}
        alt={tribute.pet.name || 'Pet Tribute Image'}
        sx={{ objectFit: 'cover' }}
        loading="lazy"
      />
      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 3,
        }}
      >
        <Box display="flex" alignItems="flex-start" mb={3}>
          <Box flexGrow={1} minWidth={0}>
            <Typography
              variant="h6"
              component="h3"
              noWrap
              sx={{ fontWeight: 600 }}
            >
              {tribute.pet.name || 'Unnamed LGCY'}
            </Typography>

            <StatusBadge status={tribute.status || 'draft'} />
          </Box>

          <IconButton
            aria-label={`More options for ${tribute.pet.name || 'LGCY'}`}
            aria-controls={open ? 'lgcy-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleMenuOpen}
            sx={{
              ml: 1,
              p: 1.25,
              borderRadius: '12px',
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <MoreVertical fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={6}>
                    Created: {createdAt}
        </Typography>
        <Button
          component={Link}
          to={primaryAction.href}
          variant="contained"
          color="primary"
          startIcon={<primaryAction.Icon />}
          sx={{
            mt: 'auto',
            borderRadius: '12px',
            textTransform: 'none',
            minHeight: '44px',
            bgcolor: 'rgb(242, 110, 99)',
            '&:hover': {
              bgcolor: 'rgb(220, 95, 85)',
            },
          }}
          aria-label={`${primaryAction.label} for ${tribute.pet.name || 'LGCY'}`}
        >
          {primaryAction.label}
        </Button>
      </CardContent>
      <Menu
        id="lgcy-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ 'aria-labelledby': 'more-button' }}
      >
        <MenuItem
          component={Link}
          to={router.tributes.edit(tribute.id).path}
          onClick={handleMenuClose}
        >
          <Edit fontSize="small" sx={{ mr: 2 }} />
          Edit this LGCY
        </MenuItem>

        <MenuItem
          onClick={onCreateCard}
        >
          <CreditCard fontSize="small" sx={{ mr: 2 }} />
                    Order LGCY Card
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default TributeCard;
